import React, { useEffect, useState } from "react";
import {
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useAuthFetch } from "../../../../utils/useAuthFetch";
import { useGroups } from "../../hooks/useGroups";

type GrantLevel = "inherit" | "none" | "read" | "read-write";
interface ElementGrant {
  group_id: string;
  level: Exclude<GrantLevel, "inherit">;
}

interface ElementGrantsEditorProps {
  workspaceId: string;
  canvasId: string;
  elementId: string;
}

/** Admin-only control. The API checks role and element ownership on every request. */
export default function ElementGrantsEditor({
  workspaceId,
  canvasId,
  elementId,
}: ElementGrantsEditorProps) {
  const authFetch = useAuthFetch();
  const {
    groups,
    loading: groupsLoading,
    error: groupsError,
  } = useGroups(workspaceId);
  const [grants, setGrants] = useState<ElementGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingGroupId, setSavingGroupId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const url = `/api/workspace/myvault/canvases/${canvasId}/elements/${elementId}/grants`;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setGrants([]);
    authFetch(url, "load element grants", {
      headers: { "x-workspace-id": workspaceId },
    })
      .then((result: ElementGrant[]) => {
        if (active) setGrants(result);
      })
      .catch((cause) => {
        if (active)
          setError(
            cause instanceof Error
              ? cause.message
              : "Failed to load element grants",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [authFetch, url, workspaceId]);

  const updateGrant = async (groupId: string, level: GrantLevel) => {
    setSavingGroupId(groupId);
    setError(null);
    try {
      const result: ElementGrant | null = await authFetch(
        url,
        "update element grant",
        {
          method: "PUT",
          headers: {
            "x-workspace-id": workspaceId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ group_id: groupId, level }),
        },
      );
      setGrants((current) => [
        ...current.filter((grant) => grant.group_id !== groupId),
        ...(result ? [result] : []),
      ]);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Failed to update element grant",
      );
    } finally {
      setSavingGroupId(null);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Typography level="title-sm">Group access</Typography>
      <Typography level="body-xs">
        Inherit uses the parent element&apos;s access. None explicitly denies
        access for this group.
      </Typography>
      {(groupsError || error) && (
        <Alert color="danger">{groupsError || error}</Alert>
      )}
      {(groupsLoading || loading) && (
        <CircularProgress size="sm" aria-label="Loading element grants" />
      )}
      {!groupsLoading &&
        !loading &&
        groups.map((group) => (
          <FormControl key={group.id}>
            <FormLabel>{group.display_name}</FormLabel>
            <Select<GrantLevel>
              value={
                grants.find((grant) => grant.group_id === group.id)?.level ||
                "inherit"
              }
              onChange={(_, value) => {
                if (value) void updateGrant(group.id, value);
              }}
              disabled={!!savingGroupId || !!groupsError || !!error}
              aria-label={`${group.display_name} element access`}
            >
              <Option value="inherit">Inherit</Option>
              <Option value="none">None</Option>
              <Option value="read">Read</Option>
              <Option value="read-write">Read and write</Option>
            </Select>
          </FormControl>
        ))}
    </Stack>
  );
}
